import React from "react";
import { Helmet } from "react-helmet";

import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  Film,
  Sparkles,
  Tv,
  Volume2,
  Armchair,
  UtensilsCrossed,
  Camera,
  Play,
  CheckCircle2,
  Layers,
  ArrowRight,
  ShieldCheck,
  Clapperboard,
  Sliders,
  Users
} from "lucide-react";


import OccasionsNav from "../components/OccasionsNav";
import { MEDIA, mediaUrl } from "../config/media";

const HeroVideo = React.memo(() => {
  const r = React.useRef(null);
  const [playing, setPlaying] = React.useState(true);
  const [muted, setMuted] = React.useState(true);

  React.useEffect(() => {
    if (r.current) {
      r.current.muted = true;
      r.current.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }, []);

  const togglePlay = () => {
    if (!r.current) return;
    if (r.current.paused) {
      r.current.muted = false;
      setMuted(false);
      r.current.play().then(() => setPlaying(true)).catch(() => {});
    } else if (muted) {
      r.current.muted = false;
      setMuted(false);
    } else {
      r.current.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="kp-hero-vid-wrap" onClick={togglePlay} style={{ cursor: "pointer" }}>
      <video
        ref={r}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        src={mediaUrl(MEDIA.occasions.privateTheatres) || "/assets/videos/Private_Theatres.mp4"}
        poster="/assets/img/screening.jpg"
        muted={muted}
        autoPlay
        loop
        playsInline
        preload="auto"
        onError={(e) => {
          if (e.target.src !== "/assets/videos/Private_Theatres.mp4") {
            e.target.src = "/assets/videos/Private_Theatres.mp4";
          }
        }}
      />
      <div className="kp-hero-vid-ovl" />
      {(!playing || muted) && (
        <div className="cc-play-button-wrapper">
          <div className="cc-play-pulse" />
          <button className="cc-play-btn" aria-label="Play">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
          </button>
        </div>
      )}
      <div className="kp-hero-vid-badge">
        <div className="kp-hero-vid-pulse" />
        <span className="kp-hero-vid-label">🎬 Real Movie Screenings & Creator Premieres</span>
        {muted && <span style={{ marginLeft: "auto", fontSize: 10, color: "rgba(255,255,255,0.75)" }}>Tap to Unmute 🔊</span>}
      </div>
    </div>
  );
});

export default function MoviePreviewPage() {


  const screeningFormats = [
    {
      icon: <Film className="w-6 h-6 text-[#E6C665]" />,
      title: "Film Preview",
      tagline: "For Independent Filmmakers & Cast",
      desc: "Screen your rough cut, final master, or test screening before festival submissions and official releases in an intimate, calibrated theatre environment.",
      specs: ["4K DCI Calibration", "Private Crew QA", "Controlled Lighting"]
    },
    {
      icon: <Clapperboard className="w-6 h-6 text-[#E6C665]" />,
      title: "Trailer Launch",
      tagline: "High-Impact Press & Influencer Reveals",
      desc: "Roll out teaser trailers and promotional reels to media reps, influencers, and brand partners with theater-grade surround sound and dramatic lighting.",
      specs: ["Mic & Host Audio", "Custom Marquee Intro", "Red Carpet Arrival"]
    },
    {
      icon: <Sliders className="w-6 h-6 text-[#E6C665]" />,
      title: "Short Film Showcase",
      tagline: "Premiere Your Vision on the Big Screen",
      desc: "Give your short film the cinema treatment it deserves. Invite friends, family, and industry peers to celebrate your hard work on a 20-foot screen.",
      specs: ["Lossless Audio Playback", "Director Intro Moment", "Photo Ops"]
    },
    {
      icon: <Tv className="w-6 h-6 text-[#E6C665]" />,
      title: "Creator Screening",
      tagline: "YouTube, OTT & Documentary Previews",
      desc: "Exclusive watch parties for YouTube series finales, pilot episodes, podcast specials, and creator documentary premieres with your core supporters.",
      specs: ["HDMI / Cloud Input", "Superfan Meet & Greet", "Gourmet Concessions"]
    },
    {
      icon: <Users className="w-6 h-6 text-[#E6C665]" />,
      title: "Private Premiere",
      tagline: "For Couples, Families & Movie Lovers",
      desc: "Watch your favorite blockbuster, timeless classic, or personal family milestone documentary in total luxury with nobody else in the room.",
      specs: ["Plush Loungers", "Celebration Decor", "Personal Movie Concierge"]
    }
  ];

  const experienceFeatures = [
    {
      icon: <Tv className="w-5 h-5 text-[#E6C665]" />,
      title: "4K / Ultra HD",
      desc: "High-contrast laser projection with calibrated cinema colors, true blacks, and razor-sharp textures."
    },
    {
      icon: <Volume2 className="w-5 h-5 text-[#E6C665]" />,
      title: "Premium Sound",
      desc: "Multi-channel Dolby spatial surround audio delivering booming subwoofers and crystal-clear dialogue."
    },
    {
      icon: <ShieldCheck className="w-5 h-5 text-[#E6C665]" />,
      title: "Private Theatre",
      desc: "100% reserved auditorium with zero outside audience, total privacy, and personalized room control."
    },
    {
      icon: <Armchair className="w-5 h-5 text-[#E6C665]" />,
      title: "Comfortable Seating",
      desc: "Plush leather recliners and extra-deep loungers providing first-class comfort for feature viewings."
    },
    {
      icon: <Sparkles className="w-5 h-5 text-[#E6C665]" />,
      title: "Celebration Décor",
      desc: "Golden ambient spotlights, film-frame marquee touches, and custom welcome screen graphics."
    },
    {
      icon: <UtensilsCrossed className="w-5 h-5 text-[#E6C665]" />,
      title: "Food & Beverages",
      desc: "Artisanal butter cinema popcorn, hot finger bites, mocktails, and fresh snacks delivered to seats."
    },
    {
      icon: <Camera className="w-5 h-5 text-[#E6C665]" />,
      title: "Photography",
      desc: "Cinema-grade photography and event coverage to capture premiere arrivals and audience reactions."
    },
    {
      icon: <Layers className="w-5 h-5 text-[#E6C665]" />,
      title: "Optional Add-ons",
      desc: "Custom cakes, red carpet rollouts, microphone setups for director Q&A, and gift hampers."
    }
  ];

  const howItWorksSteps = [
    {
      num: "01",
      title: "Choose Your Theatre",
      desc: "Pick your preferred location across Gachibowli, Kondapur, or Miyapur."
    },
    {
      num: "02",
      title: "Pick Date & Time",
      desc: "Reserve an exclusive 2 to 4-hour private slot that fits your schedule."
    },
    {
      num: "03",
      title: "Share Screening Details",
      desc: "Connect via high-speed HDMI, secure cloud, pen drive, or OTT account."
    },
    {
      num: "04",
      title: "Customize Your Experience",
      desc: "Add custom snacks, red carpet signage, or special occasion surprises."
    },
    {
      num: "05",
      title: "Confirm Your Booking",
      desc: "Instant confirmation with your personal theatre concierge assigned."
    }
  ];

  return (
    <div style={{ backgroundColor: "#0C0114", color: "#F0EBF5", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif" }}>
      <Helmet>
        <title>Movie Preview & Private Screenings | Carnival Castle Hyderabad</title>
        <meta
          name="description"
          content="Your Story. Your Screen. Your Private Premiere. Host private film previews, trailer launches, short films & creator screenings in luxury 4K private theatres."
        />
      </Helmet>

      <Header />
      <OccasionsNav currentPath="/movie-preview" />

      {/* ── STYLES ── */}
      <style>{`
        
        .kp-hero-vid-wrap{position:relative;width:100%;overflow:hidden;background:#000;box-shadow:0 24px 60px rgba(0,0,0,.7),0 0 50px rgba(94,14,115,.3);border:1.5px solid rgba(201,168,76,.4);transition:all .35s ease;}
@media(min-width:768px){.kp-hero-vid-wrap{aspect-ratio:16/9;max-width:580px;border-radius:20px;margin:0 auto;}}
@media(max-width:767px){.kp-hero-vid-wrap{aspect-ratio:9/16;max-width:320px;border-radius:24px;margin:0 auto;}}
        .kp-hero-vid-ovl{position:absolute;inset:0;background:linear-gradient(180deg,transparent 60%,rgba(12,1,20,.85) 100%);pointer-events:none;}
        .kp-hero-vid-badge{position:absolute;bottom:14px;left:14px;right:14px;background:rgba(12,1,20,.85);backdrop-filter:blur(8px);border:1px solid rgba(230,198,101,.35);border-radius:12px;padding:9px 14px;display:flex;align-items:center;gap:10px;z-index:2;}
        .kp-hero-vid-pulse{width:8px;height:8px;border-radius:50%;background:#EF4444;flex-shrink:0;animation:kp-pulse 1.4s ease infinite;box-shadow:0 0 8px #EF4444;}
        .kp-hero-vid-label{font-size:11.5px;font-weight:600;color:#FFF0B8;}
        @keyframes kp-pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.5);opacity:.5}}

        @keyframes vts-pulse { 0% { transform: translate(-50%, -50%) scale(1); opacity: .65; } 100% { transform: translate(-50%, -50%) scale(1.85); opacity: 0; } }
        .cc-play-button-wrapper { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 72px; height: 72px; z-index: 5; pointer-events: all; cursor: pointer; }
        .cc-play-pulse { position: absolute; left: 50%; top: 50%; width: 100%; height: 100%; border-radius: 50%; border: 1.5px solid rgba(201, 168, 76, 0.65); transform: translate(-50%, -50%); pointer-events: none; animation: vts-pulse 2.5s ease-out infinite; }
        .cc-play-btn { position: absolute; inset: 0; width: 100%; height: 100%; border-radius: 50%; background: rgba(10, 0, 20, 0.55); border: 1.5px solid rgba(201, 168, 76, 0.5); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; transition: all .3s cubic-bezier(.25,1,.5,1); box-shadow: 0 10px 40px rgba(0,0,0,.6); cursor: pointer; }
        .cc-play-btn:hover { transform: scale(1.08); border-color: #C9A84C; background: rgba(94, 14, 115, 0.65); box-shadow: 0 14px 45px rgba(201, 168, 76, 0.4); }
        .cc-play-btn svg { fill: #fff; }

        .mp-gold-gradient {
          background: linear-gradient(135deg, #FFF0B8 0%, #E6C665 50%, #B8860B 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .mp-film-border {
          position: relative;
          border: 1px solid rgba(230, 198, 101, 0.22);
          background: radial-gradient(circle at 50% 0%, rgba(110, 26, 133, 0.15) 0%, rgba(12, 1, 20, 0.95) 75%);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.08);
        }
        .mp-film-border:hover {
          border-color: rgba(230, 198, 101, 0.45);
        }
        .mp-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(230, 198, 101, 0.12);
          border: 1px solid rgba(230, 198, 101, 0.35);
          color: #E6C665;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          padding: 5px 14px;
          border-radius: 40px;
        }
        .mp-btn-primary {
          background: linear-gradient(135deg, #E6C665 0%, #D4AF37 50%, #996515 100%);
          color: #0C0114;
          font-weight: 700;
          font-size: 14px;
          letter-spacing: 0.3px;
          padding: 12px 26px;
          border-radius: 12px;
          border: none;
          cursor: pointer;
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 4px 18px rgba(212, 175, 55, 0.3);
          text-decoration: none;
        }
        .mp-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 26px rgba(212, 175, 55, 0.5);
          color: #000;
        }
        .mp-btn-outline {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(230, 198, 101, 0.3);
          color: #FAF4EB;
          font-weight: 600;
          font-size: 14px;
          padding: 12px 22px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
        }
        .mp-btn-outline:hover {
          background: rgba(230, 198, 101, 0.12);
          border-color: #E6C665;
          color: #FFF;
        }
        .mp-sprocket {
          display: flex;
          gap: 10px;
          padding: 8px 16px;
          background: rgba(0, 0, 0, 0.4);
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        }
        .mp-sprocket-hole {
          width: 10px;
          height: 8px;
          border-radius: 2px;
          background: rgba(230, 198, 101, 0.25);
        }
        .mp-step-line {
          position: absolute;
          top: 24px;
          left: 40px;
          right: 40px;
          height: 2px;
          background: linear-gradient(90deg, #E6C665, rgba(230, 198, 101, 0.2));
          z-index: 0;
        }
        @media(max-width: 991px) {
          .mp-step-line { display: none; }
        }
      `}</style>

      {/* ── SECTION 1: HERO (Cinematic Premiere) ── */}
      <section style={{ position: "relative", paddingTop: "50px", paddingBottom: "60px", overflow: "hidden" }}>
        {/* Subtle radial light beam from top */}
        <div
          style={{
            position: "absolute",
            top: "-80px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "600px",
            height: "400px",
            background: "radial-gradient(circle, rgba(94, 14, 115, 0.35) 0%, rgba(212, 175, 55, 0.08) 45%, transparent 70%)",
            pointerEvents: "none",
            filter: "blur(40px)"
          }}
        />

        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          <div className="row align-items-center g-4">
            <div className="col-lg-7">
              <div className="mp-badge mb-3">
                <Clapperboard className="w-3.5 h-3.5" />
                Private Cinema & Premiere Suite
              </div>
              <h1
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "clamp(28px, 4.2vw, 48px)",
                  fontWeight: 800,
                  lineHeight: 1.15,
                  marginBottom: "16px",
                  color: "#FFFFFF"
                }}
              >
                Your Story. <br />
                Your Screen. <br />
                <span className="mp-gold-gradient">Your Private Premiere.</span>
              </h1>
              <p
                style={{
                  fontSize: "15px",
                  lineHeight: 1.65,
                  color: "rgba(240, 235, 245, 0.78)",
                  maxWidth: "560px",
                  marginBottom: "24px"
                }}
              >
                Turn your film, trailer or special screening into an intimate cinematic experience. Step beyond crowded
                commercial multiplexes into an acoustic-tuned private theatre with plush recliners and VIP concierge service.
              </p>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "26px" }}>
                <a href="/locations" className="mp-btn-primary">
                  <Play className="w-4 h-4 fill-current" />
                  Plan Your Preview →
                </a>
                <a href="#experience" className="mp-btn-outline">
                  Explore The Experience
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>

              {/* Trust Micro-Row */}
              <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "center", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "18px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CheckCircle2 className="w-4 h-4 text-[#E6C665]" />
                  <span style={{ fontSize: "12.5px", color: "rgba(255,255,255,0.8)" }}>100% Private Screening Room</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CheckCircle2 className="w-4 h-4 text-[#E6C665]" />
                  <span style={{ fontSize: "12.5px", color: "rgba(255,255,255,0.8)" }}>Dolby Surround Sound</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CheckCircle2 className="w-4 h-4 text-[#E6C665]" />
                  <span style={{ fontSize: "12.5px", color: "rgba(255,255,255,0.8)" }}>Hyderabad's Top Rated</span>
                </div>
              </div>
            </div>

            {/* Hero Right Visual */}
            <div className="col-lg-5" style={{ position: "relative" }}>
              <HeroVideo />
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 2: WHAT YOU CAN HOST (5 Formats) ── */}
      <section style={{ padding: "48px 0", background: "rgba(22, 4, 36, 0.75)", borderTop: "1px solid rgba(230, 198, 101, 0.12)", borderBottom: "1px solid rgba(230, 198, 101, 0.12)" }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: "36px" }}>
            <div className="mp-badge mb-2">✦ Curated Screenings ✦</div>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(24px, 3.2vw, 36px)",
                color: "#FFFFFF",
                fontWeight: 700
              }}
            >
              What You Can <span className="mp-gold-gradient">Host With Us</span>
            </h2>
            <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "14px", maxWidth: "520px", margin: "8px auto 0" }}>
              From indie film festivals to personal celebrations, our private screens adapt seamlessly to your premiere vision.
            </p>
          </div>

          <div className="row g-3">
            {screeningFormats.map((fmt, idx) => (
              <div key={idx} className={idx < 2 ? "col-lg-6" : "col-lg-4 col-md-6"}>
                <div
                  className="mp-film-border"
                  style={{
                    padding: "24px",
                    borderRadius: "14px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between"
                  }}
                >
                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "10px",
                          background: "rgba(230, 198, 101, 0.12)",
                          border: "1px solid rgba(230, 198, 101, 0.3)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        {fmt.icon}
                      </div>
                      <span style={{ fontSize: "11px", fontWeight: 700, color: "rgba(230, 198, 101, 0.7)", letterSpacing: "1px" }}>
                        0{idx + 1}
                      </span>
                    </div>

                    <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#FFFFFF", marginBottom: "4px" }}>
                      {fmt.title}
                    </h3>
                    <div style={{ fontSize: "12px", color: "#E6C665", fontWeight: 600, marginBottom: "10px" }}>
                      {fmt.tagline}
                    </div>
                    <p style={{ fontSize: "13px", lineHeight: 1.55, color: "rgba(240, 235, 245, 0.7)", marginBottom: "16px" }}>
                      {fmt.desc}
                    </p>
                  </div>

                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "12px" }}>
                    {fmt.specs.map((sp, sIdx) => (
                      <span
                        key={sIdx}
                        style={{
                          fontSize: "11px",
                          background: "rgba(255,255,255,0.05)",
                          padding: "3px 8px",
                          borderRadius: "6px",
                          color: "rgba(255,255,255,0.8)"
                        }}
                      >
                        ✓ {sp}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 3: THE SCREENING EXPERIENCE ── */}
      <section id="experience" style={{ padding: "50px 0", position: "relative" }}>
        <div className="container">
          <div className="row align-items-center g-4 mb-4">
            <div className="col-lg-6">
              <div className="mp-badge mb-2">✦ The Screening Experience ✦</div>
              <h2
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "clamp(24px, 3.2vw, 36px)",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  lineHeight: 1.2
                }}
              >
                Cinema-Grade Technology, <br />
                <span className="mp-gold-gradient">VIP Concierge Hospitality.</span>
              </h2>
            </div>
            <div className="col-lg-6">
              <p style={{ fontSize: "14px", lineHeight: 1.6, color: "rgba(240, 235, 245, 0.75)" }}>
                We believe great cinema demands uncompromised sight and sound. Carnival Castle provides professional studio
                acoustics combined with cozy private theatre styling so your guests feel both pampered and deeply immersed.
              </p>
            </div>
          </div>

          <div className="row g-3">
            {experienceFeatures.map((feat, i) => (
              <div key={i} className="col-lg-4 col-md-6">
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.03)",
                    border: "1px solid rgba(230, 198, 101, 0.15)",
                    borderRadius: "14px",
                    padding: "20px",
                    height: "100%",
                    transition: "all 0.25s ease"
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(230, 198, 101, 0.4)")}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(230, 198, 101, 0.15)")}
                >
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      borderRadius: "10px",
                      background: "rgba(94, 14, 115, 0.35)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "14px"
                    }}
                  >
                    {feat.icon}
                  </div>
                  <h4 style={{ fontSize: "16px", fontWeight: 700, color: "#FFFFFF", marginBottom: "6px" }}>
                    {feat.title}
                  </h4>
                  <p style={{ fontSize: "13px", color: "rgba(240, 235, 245, 0.65)", lineHeight: 1.5, margin: 0 }}>
                    {feat.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 4: HOW IT WORKS (01 to 05) ── */}
      <section style={{ padding: "48px 0", background: "rgba(18, 2, 30, 0.9)", borderTop: "1px solid rgba(230, 198, 101, 0.1)" }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: "36px" }}>
            <div className="mp-badge mb-2">✦ Seamless Booking ✦</div>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(24px, 3.2vw, 36px)",
                color: "#FFFFFF",
                fontWeight: 700
              }}
            >
              How Your <span className="mp-gold-gradient">Screening Works</span>
            </h2>
            <p style={{ color: "rgba(255,255,255,0.65)", fontSize: "14px", maxWidth: "480px", margin: "8px auto 0" }}>
              Five simple steps to lock in your private auditorium for a smooth, stress-free showcase.
            </p>
          </div>

          <div style={{ position: "relative" }}>
            <div className="mp-step-line" />
            <div className="row g-3" style={{ position: "relative", zIndex: 1 }}>
              {howItWorksSteps.map((step, idx) => (
                <div key={idx} className="col-lg col-md-4 col-6">
                  <div
                    style={{
                      background: "rgba(12, 1, 20, 0.9)",
                      border: "1px solid rgba(230, 198, 101, 0.2)",
                      borderRadius: "14px",
                      padding: "20px 16px",
                      height: "100%",
                      textAlign: "center"
                    }}
                  >
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #E6C665, #B8860B)",
                        color: "#0C0114",
                        fontWeight: 800,
                        fontSize: "13px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 12px"
                      }}
                    >
                      {step.num}
                    </div>
                    <h5 style={{ fontSize: "14px", fontWeight: 700, color: "#FFFFFF", marginBottom: "6px" }}>
                      {step.title}
                    </h5>
                    <p style={{ fontSize: "12px", color: "rgba(255,255,255,0.6)", lineHeight: 1.45, margin: 0 }}>
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 5: FINAL CTA SECTION ── */}
      <section style={{ padding: "55px 0", position: "relative", overflow: "hidden" }}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(circle at 50% 50%, rgba(94, 14, 115, 0.3) 0%, rgba(12, 1, 20, 0.95) 75%)",
            pointerEvents: "none"
          }}
        />

        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          <div
            className="mp-film-border"
            style={{
              padding: "40px 24px",
              borderRadius: "20px",
              textAlign: "center",
              maxWidth: "880px",
              margin: "0 auto"
            }}
          >
            <div className="mp-badge mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              Private Screenings Made Effortless
            </div>

            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(26px, 3.8vw, 42px)",
                color: "#FFFFFF",
                fontWeight: 800,
                marginBottom: "14px",
                lineHeight: 1.2
              }}
            >
              Ready For Your <span className="mp-gold-gradient">Private Premiere?</span>
            </h2>

            <p
              style={{
                fontSize: "15px",
                color: "rgba(240, 235, 245, 0.75)",
                maxWidth: "540px",
                margin: "0 auto 26px",
                lineHeight: 1.6
              }}
            >
              Select your favorite Carnival Castle private theatre destination, pick your ideal time slot, and let our
              concierge set up the red carpet for you.
            </p>

            <div style={{ display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
              <a href="/locations" className="mp-btn-primary" style={{ padding: "14px 34px", fontSize: "15px" }}>
                <Film className="w-4 h-4" />
                Plan Your Preview →
              </a>
              <a
                href="https://wa.me/918341428342?text=Hi%20Carnival%20Castle,%20I'm%20interested%20in%20booking%20a%20private%20Movie%20Preview%20screening."
                target="_blank"
                rel="noreferrer"
                className="mp-btn-outline"
                style={{ padding: "14px 24px", fontSize: "14px" }}
              >
                Chat with Cinema Concierge
              </a>
            </div>

            <div style={{ marginTop: "22px", fontSize: "12px", color: "rgba(255,255,255,0.45)" }}>
              Instant Booking Confirmation • Zero Technical Hassle • Dedicated Host Onsite
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
